/**
 * P09 next step for every arrival (search + AI program 2026-10-23).
 *
 * The mobile fold under the price carries the module:
 *   "Ask about this home"                  Sarah chat, the contact form when Sarah is not mounted
 *   "Get price-change alerts for this home" email only (price-drop watch)
 *   "Schedule a tour"                      the tour (Matt 2026-11-01, was "Walk through it")
 *   "Call" · "Text"                        the contact broker's business line, two buttons
 *   "See {N} similar homes in {place}"     the similar rail on this page
 *   "What's {place}'s market doing?"       the community (or town) hub
 *
 * Ask and alert lead for every arrival, so an AI-referred reader (P01 channel)
 * sees them first with no client reorder and no layout shift. The arrival is
 * still read on the client and sent with every event so the AI-referrer
 * conversion rate can be measured against its 1-of-82 baseline.
 *
 * Status alerts are not offered: submitListingPriceDropWatch stores
 * price_change only (app/actions/search-alert-capture.ts). The label says
 * price change until a status rail exists.
 *
 * GA4: no new event names (a new name needs a GTM regex republish). Every
 * event carries surface 'listing_fold' (the name the fold's events already
 * use; GA4 history stays one series), cta, listing_key, community_slug
 * and the arrival twice: `arrival` for the first-party store, and `context`
 * ('arrival_ai' | 'arrival_other') because GA4 drops a parameter the GTM tag
 * does not list (lib/analytics/ga4-browser-events.ts GA4_EVENT_PARAMS).
 */
import { classifyAiReferrer } from '@/lib/ai-referrers'

export const LISTING_NEXT_STEP_SURFACE = 'listing_fold'

export type ListingArrival = 'ai' | 'other'

export type ListingNextStepCta = 'ask' | 'price_alert' | 'tour' | 'similar' | 'market'

export type ListingNextStepLink = { href: string; label: string }

export type ListingNextStepLinks = {
  similar: ListingNextStepLink | null
  market: (ListingNextStepLink & { kind: 'community' | 'town' }) | null
}

/** AI arrival from the referrer host or utm_source (ChatGPT adds utm_source=chatgpt.com). */
export function listingArrival(referrer: string | null | undefined, search: string | null | undefined): ListingArrival {
  // classifyAiReferrer reads a GA4 source (a bare host); document.referrer is a URL.
  let referrerHost: string | null = null
  try {
    referrerHost = referrer ? new URL(referrer).hostname : null
  } catch {
    referrerHost = null
  }
  if (classifyAiReferrer(referrerHost)) return 'ai'
  let utm: string | null = null
  try {
    utm = new URLSearchParams(search ?? '').get('utm_source')
  } catch {
    utm = null
  }
  return classifyAiReferrer(utm) ? 'ai' : 'other'
}

function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`
}

export function askNextStepLabel(kind: 'home' | 'lease' | 'land'): string {
  if (kind === 'land') return 'Ask about this lot'
  if (kind === 'lease') return 'Ask about this rental'
  return 'Ask about this home'
}

export function alertNextStepLabel(kind: 'home' | 'lease' | 'land'): string {
  if (kind === 'land') return 'Get price-change alerts for this lot'
  if (kind === 'lease') return 'Get price-change alerts for this listing'
  return 'Get price-change alerts for this home'
}

export function similarNextStepLabel(count: number, place: string): string {
  return `See ${count} similar ${count === 1 ? 'home' : 'homes'} in ${place}`
}

export function marketNextStepLabel(place: string): string {
  return `What's ${possessive(place)} market doing?`
}

/**
 * The two onward links. Similar renders only when the rail rendered on this
 * page (count > 0, anchor href) and names the rail's own place; market links
 * the first up-link hub, community before town.
 */
export function listingNextStepLinks(input: {
  similarCount: number
  similarPlace: string | null | undefined
  similarHref: string
  hubs: ReadonlyArray<{ kind: 'community' | 'town'; href: string; name: string }>
}): ListingNextStepLinks {
  const place = input.similarPlace?.trim() ?? ''
  const similar =
    input.similarCount > 0 && place && input.similarHref.startsWith('#')
      ? { href: input.similarHref, label: similarNextStepLabel(input.similarCount, place) }
      : null
  const hub = input.hubs[0]
  const market = hub ? { kind: hub.kind, href: hub.href, label: marketNextStepLabel(hub.name) } : null
  return { similar, market }
}

/** The GA4 params every next-step event carries. */
export function listingNextStepParams(input: {
  cta: ListingNextStepCta
  listingKey: string
  communitySlug: string | null
  arrival: ListingArrival
}): Record<string, string> {
  const params: Record<string, string> = {
    surface: LISTING_NEXT_STEP_SURFACE,
    cta: input.cta,
    listing_key: input.listingKey,
    arrival: input.arrival,
    context: `arrival_${input.arrival}`,
  }
  if (input.communitySlug) params.community_slug = input.communitySlug
  return params
}
